
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Megaphone, X, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface AnnouncementCardProps {
  announcement: {
    id: string;
    title: string;
    content: string;
    type: string;
    display_type: string;
  };
  onDismiss?: (id: string) => void;
  onSignInRequired?: () => void;
}

export const AnnouncementCard = ({ announcement, onDismiss, onSignInRequired }: AnnouncementCardProps) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    checkUser();
  }, []);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'info': return 'bg-blue-100 text-blue-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'success': return 'bg-green-100 text-green-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onDismiss) {
      onDismiss(announcement.id);
    }
  };

  if (isDismissed) return null;

  return (
    <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0 shadow-lg">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="bg-white/20 p-2 rounded-full">
              <Megaphone className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-sm">{announcement.title}</h3>
                <Badge className={`${getTypeColor(announcement.type)} text-xs`}>
                  {announcement.type.charAt(0).toUpperCase() + announcement.type.slice(1)}
                </Badge>
                {announcement.display_type === 'sticky' && (
                  <Badge variant="secondary" className="text-xs bg-white/20 text-white border-white/30">
                    Pinned
                  </Badge>
                )}
              </div>
              <p className="text-sm leading-relaxed text-blue-50">
                {announcement.content}
              </p>
              {!user && onSignInRequired && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onSignInRequired}
                  className="mt-2 bg-white/20 text-white border-white/30 hover:bg-white/30"
                >
                  <LogIn className="h-3 w-3 mr-1" />
                  Sign In to View Details
                </Button>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="text-white hover:bg-white/10 h-6 w-6 p-0 ml-2"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
